<template>
  <div>
    <transition name="fade" mode="out-in">
      <content-container v-if="dataLoading" :key="'loader'">
        <div class="digital-service">
          <loader-with-message :has-error="dataFetchError"
                               @retry="fetchMovePackages"/>
        </div>
      </content-container>
      <content-container v-else-if="userStatus === 'BLOCKED' || userStatus === 'ON_HOLD'">
        <div class="digital-service">
          <account-status-blocked v-if="userStatus === 'BLOCKED'" :key="'status-blocked'" :resources-data="$props.resourcesData"/>
          <account-status-on-hold v-else-if="userStatus === 'ON_HOLD'" :key="'status-on-hold'" :resources-data="$props.resourcesData"/>
        </div>
      </content-container>
      <div v-else :key="'content'">
        <div class="move-header">
          <content-container>
            <div class="digital-service">
              <h2 class="move-heading">{{ $t('digitalServices.move.pageTitle') }}</h2>
              <h4 class="subtitle">{{ $t('digitalServices.move.title') }}</h4>
              <div class="app-links">
                <a href="https://apps.apple.com/gr/app/move-tv/id1628850139" target="_blank" rel="nofollow">
                  <img class="store-button" src="/assets/images/app-store-badge.png" alt="app-store">
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.mtssi.supernova" target="_blank" rel="nofollow">
                  <img class="store-button" src="/assets/images/google-play-badge.png" alt="google-play">
                </a>
              </div>
            </div>
          </content-container>
        </div>
        <content-container class="move-lower-section">
          <div class="digital-service">
            <div class="card-wrapper" :class="{'full-width': isPending}" v-if="Object.keys(availableMoveService).length">
              <template v-if="!isPending">
                <digital-service-card :description="availableMoveService.description"
                                      :logo="getLogoUrl"
                                      :price="getPrice"
                                      :tooltip="availableMoveService.tooltipDescription"
                                      :service-active="availableMoveService.active"
                                      @activate-service="activateService"
                                      @deactivate-service="deactivateService"
                                      recurring-price
                                      :activation-disabled="isPending"
                />
                <benefits-card :title="$t('digitalServices.move.benefitsCard.title')"/>
              </template>
              <move-pending-status v-else/>
            </div>
            <div v-else class="card-wrapper-placeholder"></div>

            <move-credentials
              v-if='moveUsername && movePassword'
              :username='moveUsername'
              :password='movePassword'
            :move-service-id='availableMoveService.moveServiceId'/>

            <div class="move-list-title">
              <base-heading v-if="totalDevices" :underline="true">
                {{ $t('digitalServices.ListOfDevices') }}
                <span class="heading-helper">
                  {{ $t('digitalServices.move.devices.activeDevices') }}
                  <span>{{ deviceList.length }}/{{ totalDevices }}</span>
                </span>
              </base-heading>
            </div>
            <div class="move-device-list">
              <div v-for="(device, index) in deviceList" class="move-device-item" :key="'device-list' + index">
                <options-management>
                  <img slot="icon" src="/assets/images/tv-devices.png" alt="tv"/>
                  <base-button v-if="device.deviceId" variant="text" slot="action" class="manual-button" @click="setDeviceToUnassign(device)">
                    {{ $t('digitalServices.move.devices.removeDevice') }}
                  </base-button>
                  <options-item>
                    <template #description>
                      <p class="option-description">
                        {{ $t('optionsManagement.optionNameLabel') }}
                      </p>
                      <h4 class="option-title">
                        {{ device.deviceName }}
                      </h4>
                    </template>
                  </options-item>
                  <options-item>
                    <template #description>
                      <p class="option-description">
                        {{ $t('digitalServices.move.devices.macAddress') }}
                      </p>
                      <h4 class="option-title">
                        {{ device.macAddress }}
                      </h4>
                    </template>
                  </options-item>
                </options-management>
              </div>
              <template v-if="availableDeviceSlots">
                <div v-for="index in availableDeviceSlots" class="move-device-slot" :key="'device-placeholder' + index">
                  <h4 class="move-device-slot-title">{{ $t('digitalServices.move.devices.availableSlot') }}</h4>
                  <p>{{ $t('digitalServices.move.devices.availableSlotDesc') }}</p>
                </div>
              </template>
            </div>
          </div>
        </content-container>
      </div>
    </transition>
    <!-- confirmation modal -->
    <confirmation-modal :visible="moveActivationModal"
                        :closeButtonVisible="false"
                        @close="moveActivationModal = false"
                        :cancel-button="true"
                        confirm-type="empty"
                        :confirm-title="$t('digitalServices.move.confirmationModal.title')"
                        :confirm-description="$t('digitalServices.move.confirmationModal.description')"
                        :confirm-text="$t('common.activate')"
                        :loading="confirmationLoader"
                        @confirm="activateMoveApi"
                        :button-disabled="buttonDisabled"/>
    <!-- success modal -->
    <confirmation-modal :visible="moveSuccessActivation"
                        @close="resetData"
                        confirm-type="empty"
                        :confirm-title="$t('digitalServices.move.successActivationModal.title')"
                        :confirm-description="$t('digitalServices.move.successActivationModal.description')"
                        :cancel-button="false"
                        :confirm-button="false">
      <div slot="extra-section">
        <div class="app-links">
          <a href="https://apps.apple.com/gr/app/move-tv/id1628850139" target="_blank" rel="nofollow">
            <img class="store-button" src="/assets/images/app-store-badge.png" alt="app-store">
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.mtssi.supernova" target="_blank" rel="nofollow">
            <img class="store-button" src="/assets/images/google-play-badge.png" alt="google-play">
          </a>
        </div>
      </div>
    </confirmation-modal>

    <!-- deactivation modal -->
    <confirmation-modal :visible="deactivationModal"
                        :closeButtonVisible="false"
                        @close="deactivationModal = false"
                        :cancel-button="true"
                        confirm-type="empty"
                        :confirm-title="$t('digitalServices.move.deactivationModal.title')"
                        :confirm-description="$t('digitalServices.move.deactivationModal.description')"
                        :confirm-text="$t('common.deactivate')"
                        :loading="confirmationLoader"
                        @confirm="deactivateServiceApi"
                        :button-disabled="buttonDisabled"/>
    <!-- unassign modal -->
    <confirmation-modal :visible="!!deviceToUnassign"
                        :closeButtonVisible="false"
                        @close="deviceToUnassign = null"
                        :cancel-button="true"
                        confirm-type="empty"
                        :confirm-title="$t('digitalServices.move.confirmationModal.unassignTitle')"
                        :confirm-description="$t('digitalServices.move.confirmationModal.unassignDescription')"
                        :confirm-text="$t('digitalServices.move.confirmationModal.unassignButton')"
                        @confirm="unassignMoveApi"
                        :button-disabled="buttonDisabled"/>

    <digital-instruction-sticky-button service-name="MOVE"/>
  </div>
</template>

<script>

import errorMessageHandler from '@/mixins/errorMessageHandler'
import ConfirmationModal from '@/components/common/modal/confirmation-modal'
import DigitalServiceCard from '@/components/digital-services/digital-service-card'
import BenefitsCard from '@/components/digital-services/benefits-card'
import OptionsManagement from '@/components/common/options-management/options-management'
import OptionsItem from '@/components/common/options-management/options-item'
import ContentContainer from '@/components/common/main-layout/content-container'
import LoaderWithMessage from '@/components/common/loader-with-message'
import DigitalInstructionStickyButton from '@/components/digital-services/digital-instruction-sticky-button'
import AccountStatusOnHold from '@/components/services/account-status/account-status-on-hold'
import AccountStatusBlocked from '@/components/services/account-status/account-status-blocked'
import MovePendingStatus from '@/components/digital-services/move-pending-status'
import MoveCredentials from './move-credentials.vue'

export default {
  name: 'digital-service-move',
  components: {
    MoveCredentials,
    MovePendingStatus,
    DigitalInstructionStickyButton,
    LoaderWithMessage,
    ContentContainer,
    ConfirmationModal,
    DigitalServiceCard,
    BenefitsCard,
    OptionsManagement,
    OptionsItem,
    AccountStatusOnHold,
    AccountStatusBlocked
  },
  props: {
    resourcesData: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  mixins: [errorMessageHandler],
  data () {
    return {
      isPending: false,
      moveSuccessActivation: false,
      moveActivationModal: false,
      deactivationModal: false,
      dataLoading: true,
      dataFetchError: false,
      buttonDisabled: false,
      availableMoveService: {},
      deviceList: [],
      totalDevices: null,
      userStatus: '',
      customerId: '',
      deviceToUnassign: null,
      confirmationLoader: false,
      moveUsername: '',
      movePassword: ''
    }
  },
  computed: {
    getLogoUrl () {
      return this.availableMoveService.image.url
    },
    getPrice () {
      return this.availableMoveService.originalPrice.formattedValue
    },
    availableDeviceSlots () {
      return this.totalDevices - this.deviceList.length
    }
  },
  methods: {
    fetchMovePackages () {
      if (this.$props.resourcesData.subscriptionInfo && this.$props.resourcesData.subscriptionInfo.customerId) {
        this.dataLoading = true
        this.dataFetchError = false
        const customerId = this.$props.resourcesData.subscriptionInfo.customerId
        this.customerId = customerId
        this.$apiRequest.get(`user/move?customerId=${this.customerId}`)
          .then(response => {
            this.availableMoveService = response.data.availableMoveService || {}
            this.isPending = !!response.data.pendingDate
            this.deviceList = response.data.devices || []
            this.totalDevices = response.data.totalDevices
            this.userStatus = response.data.contractStatus
            this.moveUsername = response.data?.username || ''
            this.movePassword = response.data?.password || ''
            this.dataLoading = false
          })
          .catch(e => {
            this.dataFetchError = true
          })
      }
    },
    activateService () {
      this.moveActivationModal = true
    },
    activateMoveApi () {
      this.buttonDisabled = true
      this.confirmationLoader = true
      const dataToSend = {
        customerId: this.customerId,
        activate: true
      }
      this.$apiRequest.post('user/move', dataToSend)
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            this.moveActivationModal = false
            this.moveSuccessActivation = true
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.buttonDisabled = false
          this.confirmationLoader = false
        })
    },
    resetData () {
      this.moveSuccessActivation = false
      this.fetchMovePackages()
    },
    deactivateService () {
      this.deactivationModal = true
    },
    deactivateServiceApi () {
      this.buttonDisabled = true
      this.confirmationLoader = true
      const dataToSend = {
        customerId: this.customerId,
        activate: false
      }
      this.$apiRequest.post('user/move', dataToSend)
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            this.deactivationModal = false
            this.toast('success', {
              title: this.$t('toastMessages.successToastTitle'),
              message: this.$t('toastMessages.successMoveDeactivationMessage')
            })
            this.fetchMovePackages()
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.buttonDisabled = false
          this.confirmationLoader = false
        })
    },
    setDeviceToUnassign (device) {
      this.deviceToUnassign = device
    },
    unassignMoveApi () {
      this.buttonDisabled = true
      const dataToSend = {
        deviceId: this.deviceToUnassign.deviceId,
        moveServiceId: this.availableMoveService.moveServiceId
      }
      this.$apiRequest.post('user/move/device', dataToSend)
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            this.deviceToUnassign = null
            this.fetchMovePackages()
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.buttonDisabled = false
        })
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.fetchMovePackages()
    })
  }
}
</script>

<style scoped lang="scss" src="./styles/digital-service-page.scss"></style>
